import React from 'react';
import PropTypes from 'prop-types';

import { Form } from 'formik';

import customFieldTypes from '~/modules/customFieldTypes';
import { checkFeatureFlag } from '~/modules/helpers';
import { isObject } from '~/modules/helpers/object';
import { toErrorMessage } from '~/modules/helpers/error';
import { RE } from '~/modules/helpers/regexp';

import AutomaticAction from '~/models/AutomaticAction';
import ObjectModel from '~/models/Object';

import Translate from '~/components/Translate/Translate';
import { toolbars } from '~/components/Editor2/Editor2';
import PageOverlay from '~/components/PageOverlay/PageOverlay';
import HeaderModal from '~/components/HeaderModal/HeaderModal';

import IncidentContext from '~/pages/IncidentDetails/IncidentContext';

import { formatNodeTitle } from '~/containers/RunbookCanvas/Graph/setNodeValues';

import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';

/**
 * The token list result
 * @typedef {Object} TokenList
 * @property {Array} incidentTriage
 * @property {Array} incidentArtifacts
 * @property {Array} incidentFields
 * @property {Object} tokenList
 */

/**
 * Prepare token list for select option and textarea placeholder
 * @param {Object} list
 * @returns {TokenList}
 * @author sst
 */
export const prepareTokenList = list => {
    const preparedList = {
        incidentArtifacts: [],
        incidentTriage: [],
        incidentFields: [],
        tokenList: {} // "tokens"
    };
    if (list) {
        const listEntries = Object.entries(list);
        if (listEntries.length) {
            const tokens = preparedList.tokenList;
            //# 𝘧𝘭𝘰𝘸
            const arr = [
                'Playbook',
                'incidentFields',
                'incidentArtifacts',
                'triageFields'
            ]; // todo: find a better name
            for (const token in list) {
                if (!arr.includes(token)) {
                    const values = list[token];
                    //sst: get node title formatted (if there are a placeholder)
                    const nodeName = formatNodeTitle(token, {
                        plainText: true
                    });
                    for (const item of values) {
                        if (item) {
                            const itemName = item.name || {};
                            const path = itemName.path ?? itemName;
                            tokens[`${nodeName}.${path}`] = {
                                source: `FLOW::${item.id}::${path}`,
                                tooltip: itemName.example ?? null
                            };
                        }
                    }
                }
            }
            //# 𝘗𝘭𝘢𝘺𝘣𝘰𝘰𝘬 𝗶𝗻𝗽𝘂𝘁
            const listInputParams = list.inputParams;
            if (listInputParams?.length) {
                for (const field of listInputParams) {
                    const fieldParam = field.param;
                    tokens[`PlaybookInput.${fieldParam}`] = {
                        source: `PLAYBOOKINPUT::${fieldParam}`
                    };
                }
            }
            //# 𝘗𝘭𝘢𝘺𝘣𝘰𝘰𝘬 𝗽𝗮𝘆𝗹𝗼𝗮𝗱
            const listPlaybook = list.Playbook;
            if (listPlaybook?.length) {
                for (const field of listPlaybook) {
                    const fieldName = field.name;
                    tokens[`Playbook.${fieldName}`] = {
                        source: `PLAYBOOKPAYLOAD::${fieldName}`
                    };
                }
            }
            // vo: cse users cannot access to incident, triage or entities placeholders
            if (checkFeatureFlag('full_csoar')) {
                //# 𝘐𝘯𝘤𝘪𝘥𝘦𝘯𝘵 𝗳𝗶𝗲𝗹𝗱𝘀
                const listIncidentFields = list.incidentFields;
                if (listIncidentFields?.length) {
                    const incidentFields = preparedList.incidentFields;
                    for (const field of listIncidentFields) {
                        const fieldName = field.name;
                        const valueAndSource = `INCIDENT::${field.id}`;
                        incidentFields.push({
                            type: 'incident',
                            label: fieldName,
                            value: valueAndSource
                        });
                        tokens[`Incident.${fieldName}`] = {
                            source: valueAndSource
                        };
                    }
                }
                //# 𝘐𝘯𝘤𝘪𝘥𝘦𝘯𝘵 𝗮𝗿𝘁𝗶𝗳𝗮𝗰𝘁𝘀
                const listIncidentArtifacts = list.incidentArtifacts;
                if (listIncidentArtifacts?.length) {
                    const incidentArtifacts = preparedList.incidentArtifacts;
                    for (const field of listIncidentArtifacts) {
                        const fieldName = field.name;
                        //sst: avoid multiple artifact with same name
                        // todo: consider use "reduceToUniquesByKey" before instead filter inside loop
                        const isUnique = !incidentArtifacts.filter(
                            e => fieldName === e.label
                        ).length;
                        if (isUnique) {
                            const valueAndSource = `CEF::${field.id}`;
                            incidentArtifacts.push({
                                type: 'cef',
                                label: fieldName,
                                value: valueAndSource
                            });
                            tokens[`Incident Artifacts.${fieldName}`] = {
                                source: valueAndSource
                            };
                        }
                    }
                }
                //# Triage 𝗳𝗶𝗲𝗹𝗱𝘀
                const listTriageFields = list.triageFields;
                if (listTriageFields?.length) {
                    const incidentTriage = preparedList.incidentTriage;
                    for (const field of listTriageFields) {
                        const fieldName = field.name;
                        const valueAndSource = `TRIAGE::${field.id}`;
                        incidentTriage.push({
                            label: fieldName,
                            type: 'triage',
                            value: valueAndSource
                        });
                        tokens[`${SETTINGS.triageSectionName}.${fieldName}`] = {
                            source: valueAndSource
                        };
                    }
                }
            }
        }
    }
    return preparedList;
};

/**
 * Hold the modal logic and state leaving the Form state outside in an higher level manage by Formik
 */
class ModalWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            resources: [],
            actionsByType: [],
            showOverlay: false,
            //RN: if action is run through contextualMenu, checkbox is displayed to open result in the same or other tab
            openResultInOtherTab: true,
            isInternalIntegration: false,
            fetchingActionFieldsByResource: false
        };

        this.setValues = this.setValues.bind(this);

        this.prevResource = null;
        this.setResource = this.setResource.bind(this);
        this.setResources = this.setResources.bind(this);

        this.getResourceById = this.getResourceById.bind(this);

        this.setAction = this.setAction.bind(this);
        this.setActionType = this.setActionType.bind(this);
        this.setActionsByType = this.setActionsByType.bind(this);

        this.fetchActionFields = this.fetchActionFields.bind(this);

        //FM: bulk operations placeholder
        this.HASH_PLACEHOLDER = '__hash__';
        this.FIELD_PLACEHOLDER = '__placeholder__';
        this.ARTIFACT_PLACEHOLDER = '__artifact_placeholder__';
        this.INVALID_ACTION_PLACEHOLDER = '__REMOVE__';

        //RN: data for localStorage to permit the opening of result in another tab
        const openResultInOtherTab = localStorage.getItem(
            'openResultInOtherTab'
        );
        this.openInOtherTabLocalStorage = openResultInOtherTab;
        this.localStorageIsSetCorrectly =
            openResultInOtherTab &&
            'string' === typeof openResultInOtherTab &&
            !isNaN(parseInt(openResultInOtherTab));
        this.actionFieldsPromisesCount = 0;
    }

    componentDidMount() {
        const list = this.props.tokenList;
        if (list && Object.keys(list).length) {
            this.setState(prepareTokenList(list));
        }
        if (this.localStorageIsSetCorrectly) {
            this.setState({
                openResultInOtherTab: !!parseInt(
                    this.openInOtherTabLocalStorage
                )
            });
        }
    }

    convertToConfigurationValues(fields) {
        const prefixRegExp = new RegExp(`^${this.props.fieldPrefix}`);
        const required_fields = [];
        const opt_fields = [];
        for (const field of fields) {
            const [arr, prefix] = field.isRequired
                ? [required_fields, 'requiredField_']
                : [opt_fields, 'optField_'];
            const fieldValue = field.value;
            arr.push({
                name: fieldValue,
                value: fieldValue,
                id: `${prefix}${field.id.replace(prefixRegExp, '')}`
            });
        }
        return {
            required_fields: required_fields,
            opt_fields: opt_fields
        };
    }

    updateActionFieldsByResource(resourceValue, fields) {
        const newActionFieldsByResource = [];
        const actionFields = fields || this.props.actionFields;
        if (actionFields?.length) {
            const resourceWithPlaceholder =
                this.resourceHasPlaceholderOrInternalValue(resourceValue);

            const selectedResource = this.getResourceById(resourceValue);
            const str =
                selectedResource &&
                (selectedResource.integration_tag ||
                    selectedResource.integrationconfiguration_id);

            for (const field of actionFields) {
                if (field.hasOwnProperty('asset_type_only')) {
                    if (
                        field.asset_type_only.includes(str) ||
                        resourceWithPlaceholder
                    ) {
                        newActionFieldsByResource.push(field);
                    }
                } else {
                    console.error('has no "asset_type_only" property', field);
                }
            }
        }
        this.props.setActionFieldsByResource(newActionFieldsByResource);
        return newActionFieldsByResource;
    }

    getActionFieldsValues(actionFieldsByResource) {
        const actionFieldsValues = {};
        for (const field of actionFieldsByResource) {
            if (field.hasOwnProperty('value')) {
                actionFieldsValues[field.id] = field.value;
            }
        }
        return actionFieldsValues;
    }

    getResourceById(resourceId) {
        return this.state.resources.find(
            e => resourceId?.toString() === e.id.toString()
        );
    }

    setActionsByType(actionsByType) {
        return new Promise(resolve => {
            this.setState(
                {
                    actionsByType: actionsByType,
                    actionNameDisplay: '',
                    actionTitle: ''
                },
                () => {
                    resolve(actionsByType);
                }
            );
        });
    }

    resetAction() {
        // noinspection JSIgnoredPromiseFromCall
        this.setValues({
            action: null
        });
        this.setState({
            resources: []
        });
        return this;
    }

    resetFields() {
        let notFieldValues = {};
        const { formProps, fieldPrefix } = this.props;
        const formPropsValues = formProps?.values;
        if (formPropsValues) {
            if (fieldPrefix) {
                for (const [key, value] of Object.entries(formPropsValues)) {
                    if (!key.startsWith(fieldPrefix)) {
                        notFieldValues[key] = value;
                    }
                }
            } else {
                notFieldValues = { ...formPropsValues };
            }
        }
        return this.setValues(notFieldValues, true);
    }

    resetResource() {
        // noinspection JSIgnoredPromiseFromCall
        this.setValues({
            resource: null
        });
        this.props.setActionFieldsByResource([]);
        return this;
    }

    resetActionResourceAndField() {
        // noinspection JSIgnoredPromiseFromCall
        return this.resetAction().resetResource().resetFields();
    }

    resourceHasPlaceholderOrInternalValue(value) {
        return RE.PLAYBOOK.placeholder.flowOrPlaybookOrPlaybookPayloadOrIncidentOrTriageOrCef.test(
            value
        );
    }

    async setActionType(actionType) {
        const { formProps } = this.props;
        const formPropsValues = formProps.values;

        const newValues = {
            actionType: actionType
        };

        if (formPropsValues.actionType !== actionType) {
            await this.resetActionResourceAndField();
            //sst: force clean formProps data for action and resource
            // TODO: investigate why reset function is not enough
            newValues.action = null;
            newValues.resource = null;
        }

        await this.setValues(newValues);
        return this.setActionsByType(this.props.actionList[actionType] || []);
    }

    checkIsInternalIntegration = integrationId => {
        this.setState({
            isInternalIntegration: integrationId.match(RE.INTEGRATION.internal)
        });
    };

    // should be a sync (no awaits) arrow function:
    setIntegration = integrationTag => {
        const { formProps } = this.props;
        const formPropsValues = formProps.values;

        if (
            formPropsValues.storedIntegrationTag !==
            formPropsValues.integrationTag
        ) {
            // noinspection JSIgnoredPromiseFromCall
            this.resetActionResourceAndField();
        }

        this.checkIsInternalIntegration(integrationTag);
        const newValues = {
            integrationTag: integrationTag
        };
        // noinspection JSIgnoredPromiseFromCall
        this.setValues(newValues);
        this.props.getActionList(integrationTag);
    };

    setAction(actionInternalName, resourceId = null) {
        const formPropsValues = this.props.formProps?.values;

        return new Promise(resolve => {
            let newActionInternalName = actionInternalName;
            const resourceWithPlaceholder =
                this.resourceHasPlaceholderOrInternalValue(resourceId);
            let action = this.state.actionsByType.find(
                e => newActionInternalName === e.internalName
            );
            //# 1/2 gb: If action is behind another resource after grouping
            if (!action && resourceId) {
                let findAction = null;
                this.state.actionsByType.forEach((action, index, arr) => {
                    const findIndex = action.resources.findIndex(
                        resource =>
                            resourceId === resource.id &&
                            actionInternalName === resource.actionInternalName
                    );
                    if (findIndex > 0 && !findAction) {
                        findAction = arr[index];
                    }
                });
                if (findAction) {
                    action = findAction;
                    newActionInternalName = action.internalName;
                }
            }
            //# 2/2 sst: can't find action
            if (!action) {
                //sst: reset form values to avoid update (action not set in integrations)
                const actualFormProps = this.props.formProps; // todo: investigate if same of initial 'formPropsValues'
                const actualFormPropsValues = actualFormProps.values;
                const formPropsValuesUpdate = {
                    integrationTag: actualFormPropsValues.integrationTag ?? null
                    // actionTitle: actualFormPropsValues.actionTitle,
                    // actionType: actualFormPropsValues.actionType
                };
                // console.log('not found', { action, formPropsValuesUpdate });
                actualFormProps.setValues(formPropsValuesUpdate);
                return resolve();
            }

            const newValues = {
                action: newActionInternalName,
                actionNameDisplay: action.name
            };

            this.setValues(newValues, false, true).then(() => {
                const actionResources = action.resources;
                const actionDefaultResources = actionResources.filter(
                    e => e.isdefault && e.hasOwnProperty('isdefault')
                );
                this.setResources(actionResources, () => {
                    const resource =
                        !resourceWithPlaceholder && !resourceId
                            ? actionDefaultResources?.[0]?.id
                            : null;
                    if (resource) {
                        // console.log({ resource, newActionInternalName });
                        // mm: take the first resource saved as default
                        this.fetchActionFields(newActionInternalName).then(
                            () => {
                                const fields = this.fetchActionFields(
                                    newActionInternalName
                                ); // <<
                                this.setResource(resource, fields).then(
                                    resolve
                                );
                            }
                        );
                    }
                });
            });
        });
    }

    setResource(resourceId, fields, actionInternalName = null) {
        const formPropsValues = this.props.formProps?.values;
        return new Promise(resolve => {
            const updateFields = fields => {
                const actionFieldsByResource =
                    this.updateActionFieldsByResource(resourceId, fields);
                const newValues = this.getActionFieldsValues(
                    actionFieldsByResource
                );
                this.setValues(newValues).then(resolve);
            };
            this.resetResource()
                .resetFields()
                .then(() => {
                    this.setValues({
                        resource: resourceId
                    }).then(() => {
                        let promise = fields;
                        if ('function' !== typeof promise?.then) {
                            let arg = actionInternalName;
                            if (!arg) {
                                if (resourceId !== formPropsValues.resource) {
                                    const name =
                                        formPropsValues.actionNameDisplay;
                                    const action =
                                        this.state.actionsByType.find(
                                            e => name === e.name
                                        );
                                    arg = action?.internalName;
                                } else {
                                    arg = formPropsValues?.action;
                                }
                            }
                            promise = this.fetchActionFields(arg);
                        }
                        this.actionFieldsPromisesCount =
                            this.actionFieldsPromisesCount + 1;
                        this.setState({ fetchingActionFieldsByResource: true });
                        promise.then(updateFields).finally(() => {
                            this.actionFieldsPromisesCount =
                                this.actionFieldsPromisesCount - 1;
                            // set only when all the promises are executed.
                            if (this.actionFieldsPromisesCount === 0) {
                                this.setState({
                                    fetchingActionFieldsByResource: false
                                });
                            }
                        });
                    });
                });
        });
    }

    convertActionFieldToCustomFieldType(field, mappedField) {
        // TODO manage unmatched types (ex. upload)
        // @see class_automaticaction.php AutomaticAction::createHtml
        switch (field.type) {
            // mm: this field type is used only in the old hard-coded actions
            case 'listResourceMethod':
                // ??? <<
                break;
            case 'list':
                return customFieldTypes.LIST;
            case 'multilist':
                return customFieldTypes.MULTILIST;
            case 'tag':
                return customFieldTypes.TAGS;
            case 'date':
                return customFieldTypes.DATE;
            case 'datetime':
                return customFieldTypes.TIME;
            case 'checkbox':
                return customFieldTypes.CHECKBOX;
            case 'timeInterval':
                return customFieldTypes.TIMEINTERVAL;
            case 'number':
                return customFieldTypes.NUMERIC;
            case 'textarea':
                return customFieldTypes.TEXTAREA;
            case 'upload':
            case 'fileDetonate':
                return 'fileDetonate';
            case 'text':
            default:
                return this._getTextTypeWithValidation(field, mappedField);
        }
    }

    prepareActionFields(fields) {
        const { object: objects, formProps, isBulk } = this.props;
        const { initialValues } = formProps;

        const object = isBulk ? objects[0] : objects;

        const fieldsRelatedToObjectValue = object
            ? fields.filter(field => {
                  if (field.hasOwnProperty('observables')) {
                      const internalType = object.getInternalType();
                      return Array.isArray(internalType)
                          ? internalType.some(e =>
                                field.observables.includes(e)
                            )
                          : field.observables.includes(internalType);
                  }
              })
            : [];

        const fieldIDsRelatedToObjectValue = fieldsRelatedToObjectValue.map(
            field => field.id
        );

        const fieldPrefix = this.props.fieldPrefix;

        fields = fields.map(field => {
            const id = fieldPrefix + field.id;
            let mappedField = {
                id: id,
                name: id,
                internalName: id,
                type: '',
                placeholder: field?.placeholder,
                label: field.label,
                hint: field.hint,
                readOnly: field.disable,
                isRequired: field.required,
                asset_type_only: field.asset_type_only,
                cef_fields:
                    field.cef_fields || field.incident_artifacts || false
            };

            mappedField.type = this.convertActionFieldToCustomFieldType(
                field,
                mappedField
            );

            if (
                'runbook' === this.props.section &&
                mappedField.id in initialValues
            ) {
                mappedField.value = initialValues[mappedField.id];
            }

            let isListLike = [
                customFieldTypes.LIST,
                customFieldTypes.MULTILIST,
                customFieldTypes.TAGS
            ].includes(mappedField.type);

            if (isListLike) {
                mappedField.options = [];
                // todo: revise waterfall
                if ('object' === typeof field.value) {
                    mappedField.options = Object.keys(field.value).map(key => ({
                        value: key,
                        label: field.value[key]
                    }));
                }
                if (mappedField.cef_fields && this.props.fillListOptions) {
                    mappedField.options = [
                        {
                            id: 'manual',
                            label: 'Manual value',
                            options: []
                        },
                        {
                            id: 'artifact',
                            label: 'Artifact fields',
                            options: this.state.incidentArtifacts
                        },
                        {
                            id: 'incident',
                            label: 'Incident fields',
                            options: this.state.incidentFields
                        },
                        {
                            id: 'triage',
                            label: SETTINGS.triageSectionName,
                            options: this.state.incidentTriage
                        }
                    ];
                }
                if (field.hasOwnProperty('internal_values')) {
                    mappedField.options = Object.keys(
                        field.internal_values
                    ).map(key => ({
                        value: key,
                        label: field.internal_values[key]
                    }));
                }
                if (field.hasOwnProperty('values')) {
                    mappedField.options = Object.keys(field.values).map(
                        key => ({
                            value: key,
                            label: field.values[key]
                        })
                    );
                }
                if (
                    customFieldTypes.MULTILIST === mappedField.type &&
                    field.value &&
                    field.value !== ''
                ) {
                    mappedField.value = field.value;
                }
            } else if (mappedField.type === customFieldTypes.CHECKBOX) {
                // mm: omit internal label in checkbox
                mappedField.hideLabel = true;
            } else if (mappedField.type === customFieldTypes.TEXTAREA) {
                mappedField.config = {
                    toolbar: {
                        items: toolbars.automationStyleWithoutPlaceholder
                    },
                    autoplaceholder: {
                        tokens: this.state.tokenList
                    }
                };
                if (field.value) {
                    mappedField.value = field.value;
                }
            } else if ('fileDetonate' === mappedField.type) {
                let attachments = [];
                const files = [];
                const ctx = this.context;
                if (ctx) {
                    const attachmentsChildren = ctx.attachments?.children;
                    if (attachmentsChildren) {
                        attachments = this.viewingFiles(attachmentsChildren);
                    }
                    const ctxObjects = ctx.objects;
                    if (ctxObjects?.length) {
                        for (const object of ctxObjects) {
                            if ('file' === object.type?.toLowerCase()) {
                                files.push({
                                    label: object.endvalue ?? object.value,
                                    value: `artifact_${object.id}`
                                });
                            }
                        }
                    }
                }
                const { object } = this.props;
                if (object) {
                    files.push({
                        label: object.endvalue ?? object.value,
                        value: `artifact_${object.id}`
                    });
                }
                mappedField.options = [
                    {
                        id: 'attachments',
                        label: 'Attachments',
                        options: attachments
                    },
                    {
                        id: 'files',
                        label: 'Files',
                        options: files
                    },
                    {
                        label: 'Incident attachments',
                        value: 'det_all_incident_attachments'
                    },
                    {
                        label: 'Incident files',
                        value: 'det_all_incident_artifact'
                    }
                ];
                //sst: change type to list, this help to render field like a list
                mappedField.type = customFieldTypes.LIST;
            }

            if (field.hasOwnProperty('default') && !this.props.isEditAction) {
                //sst: add the default only when the value is empty or not set
                const fieldDefault = field.default;
                if (
                    null !== fieldDefault &&
                    (!mappedField.value || '' === mappedField.value)
                ) {
                    const fieldValues = field.values;
                    // prettier-ignore
                    switch (mappedField.type) {
                        case customFieldTypes.LIST:
                            mappedField.value =
                                mappedField.selected = {
                                    value: fieldDefault,
                                    label: fieldValues[fieldDefault]
                                };
                            break;
                        case customFieldTypes.MULTILIST:
                            mappedField.value =
                                mappedField.selected = [
                                    {
                                        value: fieldDefault,
                                        label: fieldValues[fieldDefault]
                                    }
                                ];
                            break;
                        case customFieldTypes.TAGS:
                            mappedField.value = [
                                {
                                    value: fieldDefault,
                                    label: fieldDefault
                                }
                            ];
                            break;
                        default:
                            mappedField.value = fieldDefault;
                    }
                }
            }

            //mm: if is an observable field fill with the object value
            if (object && fieldIDsRelatedToObjectValue.includes(field.id)) {
                let label = null;

                let value = object.isFile
                    ? `artifact_${object.id}`
                    : object.endvalue ?? object.value;

                if (isBulk) {
                    label = value = object.isFile
                        ? this.ARTIFACT_PLACEHOLDER
                        : this.FIELD_PLACEHOLDER;
                    //FM: assign placeholder value to field for bulk operation to convert it before submit
                    mappedField.placeholder =
                        objects.map(e => e.endvalue ?? e.value) ?? null;
                }

                const convertFieldToList = () => {
                    const defaultOption = {
                        value: value,
                        label: isBulk ? label : object.endvalue ?? object.value
                    };
                    mappedField.options = [defaultOption];
                    mappedField.value = defaultOption;
                    if (object && object.isFile && field?.observables) {
                        // create other options for each hash type:
                        let { hashes } = object;
                        if (isBulk) {
                            const objectsHashes = [];
                            for (const item of objects) {
                                objectsHashes.push(...Object.keys(item.hashes));
                            }
                            hashes = {};
                            for (const item of [...new Set(objectsHashes)]) {
                                hashes = {
                                    ...hashes,
                                    [item]: this.FIELD_PLACEHOLDER
                                };
                            }
                        }
                        // mm: reset option list to remove file name value
                        if (!field.observables.includes('file')) {
                            mappedField.options = [];
                        }
                        if (object.hasOwnProperty('hashes')) {
                            for (const hashType in hashes) {
                                if (!hashes.hasOwnProperty(hashType)) {
                                    continue;
                                }
                                if (!field.observables.includes(hashType)) {
                                    continue;
                                }
                                const hashValue = hashes[hashType];
                                mappedField.options.push({
                                    value: hashValue,
                                    label: hashType.toUpperCase()
                                });
                                if (isBulk) {
                                    mappedField.placeholder =
                                        this.HASH_PLACEHOLDER;
                                }
                            }
                        }
                        // mm: set as default the first option
                        mappedField.value = mappedField.options[0] || {};
                    }
                    mappedField.acceptNewOptions = true;
                    mappedField.type = customFieldTypes.LIST;
                };

                if (fieldIDsRelatedToObjectValue.length > 1) {
                    convertFieldToList();
                    if (mappedField.options.length <= 1) {
                        mappedField.readOnly = true;
                    }
                } else if (object.isFile) {
                    convertFieldToList();
                    if (mappedField.options <= 1) {
                        mappedField.readOnly = true;
                    }
                } else {
                    mappedField.type = customFieldTypes.TEXT;
                    mappedField.value = object.hash ?? value;
                    mappedField.readOnly = true;
                    isListLike = false;
                }
            }

            // mm: if field is an empty list convert to text field to let user execute the action
            if (
                isListLike &&
                mappedField.options.length === 0 &&
                this.props.section !== 'runbook'
            ) {
                mappedField.type = this._getTextTypeWithValidation(
                    field,
                    mappedField
                );
                const fieldValue = field.value;
                mappedField.value =
                    'string' === typeof fieldValue ? fieldValue : '';
            }

            //FM: set multilist values
            if (
                isListLike &&
                customFieldTypes.MULTILIST === mappedField.type &&
                mappedField?.options?.length !== 0 &&
                mappedField?.value &&
                'string' === typeof mappedField?.value
            ) {
                const multilistValues = mappedField.value.split(',');
                let value = null;
                if (multilistValues?.length) {
                    value = multilistValues.map(item => {
                        const selected = mappedField.options.find(
                            option => item === option.value
                        );
                        return selected
                            ? {
                                  value: selected.value,
                                  label: selected.label
                              }
                            : {
                                  value: item,
                                  label: item
                              };
                    });
                }
                mappedField.value = value;
            }

            if (
                'runbook' !== this.props.section &&
                (customFieldTypes.NUMERIC === mappedField.type ||
                    (!this.props.object &&
                        customFieldTypes.TEXT === mappedField.type))
            ) {
                mappedField.value = field.value;
            }

            //sst: force add new option for runbook action fields
            if ('runbook' === this.props.section) {
                mappedField.acceptNewOptions = true;
                mappedField.onCreateOption = value => {
                    const newOption = {
                        value: `EDITED::${value}`,
                        label: value
                    };
                    // todo: revise
                    //sst: add to manual group
                    mappedField.options.map(group => {
                        if ('manual' === group.id) {
                            group.options.push(newOption);
                        }
                        return group;
                    });
                    // noinspection JSIgnoredPromiseFromCall
                    this.setValues({
                        [mappedField.id]: newOption
                    });
                };
            }
            return mappedField;
        });
        return fields;
    }

    _getTextTypeWithValidation(field, mappedField) {
        if (field) {
            const dataType = field.data_type?.data || field.validator;
            if (dataType) {
                switch (dataType) {
                    case 'port':
                        return 'port';
                    case 'ipaddress':
                        return customFieldTypes.IP;
                    case 'url':
                        return customFieldTypes.URL;
                    case 'domain':
                        return customFieldTypes.DOMAIN;
                    case 'ip_domain':
                        return customFieldTypes.IPDOMAIN;
                    case 'e-mail':
                        return customFieldTypes.EMAIL;
                    case 'datetime':
                        return customFieldTypes.DATE;
                    case 'hash':
                        return customFieldTypes.HASH;
                    case 'sha256':
                        mappedField.hashType = 'sha256';
                        return customFieldTypes.HASH;
                    case 'md5':
                        mappedField.hashType = 'md5';
                        return customFieldTypes.HASH;
                    case 'sha1':
                        mappedField.hashType = 'sha1';
                        return customFieldTypes.HASH;
                    case 'integer':
                        mappedField.numberType = 'integer';
                        return customFieldTypes.NUMERIC;
                    case 'float':
                        return customFieldTypes.NUMERIC;
                }
            }
        }
        return customFieldTypes.TEXT;
    }

    fetchActionFields(actionInternalName) {
        const { triageId = '', resourceId = '' } = this.props.triage || {};
        const actionFieldsMappingKey = `${actionInternalName}_${triageId}_${resourceId}`;
        // check if fields are already fetched.
        if (
            this.props.actionFieldsMappingKey === actionFieldsMappingKey &&
            this.props.actionFields.length
        ) {
            return Promise.resolve(this.props.actionFields);
        }
        return new Promise((resolve, reject) => {
            AutomaticAction.getFields(actionInternalName, triageId, resourceId)
                .then(response => {
                    if (200 === response.status) {
                        const fields = this.prepareActionFields(
                            response.data.results
                        );
                        // console.log({ response, fields });
                        this.props.setActionFields(
                            fields,
                            actionFieldsMappingKey,
                            () => {
                                resolve(fields);
                            }
                        );
                    } else {
                        reject();
                    }
                })
                .catch(reject);
        });
    }

    /**
     * Set a value to without overriding not involved existing
     * @param {object} updatedValues
     * @param {boolean} replaceValues - If true replace all values with the object passed
     * @param {boolean} fromSetAction - If true setValues come from setAction method
     */
    setValues(updatedValues, replaceValues = false, fromSetAction = false) {
        return new Promise(resolve => {
            const {
                prevResource,
                props: { formProps = {} }
            } = this;
            const formPropsValues = formProps.values;
            if (!formPropsValues.resource && prevResource) {
                formProps.values.resource = prevResource;
            }
            if (fromSetAction) {
                if (!formPropsValues.actionTitle) {
                    formProps.values.actionTitle =
                        updatedValues.actionNameDisplay;
                }
            }
            if (
                updatedValues.resource &&
                updatedValues.resource !== prevResource
            ) {
                this.prevResource = updatedValues.resource;
            }
            const key = Object.keys(updatedValues)[0]; // <<
            const newValues = replaceValues
                ? updatedValues
                : {
                      ...formProps.values, // should be live reference because actually seem externally updated
                      ...updatedValues
                  };
            formProps.setValues(newValues);
            const newTouched = {
                ...formProps.touched,
                [key]: true
            };
            formProps.setTouched(newTouched);
            resolve(newValues);
        });
    }

    showOverlay = () => {
        this.setState({
            showOverlay: true
        });
    };

    hideOverlay = () => {
        this.setState({
            showOverlay: false
        });
    };

    setOpenResultInNewTabValue = (openResultInOtherTab, callback) => {
        this.setState(
            {
                openResultInOtherTab: openResultInOtherTab
            },
            callback
        );
    };

    onSubmit = () => {
        const {
            stickyAlert,
            formProps,
            actionFieldsByResource,
            fieldPrefix,
            actionFields,
            isBulk
        } = this.props;

        const prepareAndExecuteAction = object => {
            const formPropsValues = formProps.values;
            /**
             * Check if field is a placeholder for bulk operations
             * @param obj
             * @return {boolean}
             * @author FM
             */
            const checkPlaceholder = obj => {
                if (isObject(obj)) {
                    const searchKeys = [
                        this.FIELD_PLACEHOLDER,
                        this.ARTIFACT_PLACEHOLDER
                    ];
                    return Object.values(obj).some(
                        value => searchKeys.includes(value) // todo: consired replace includes with a direct comparison
                    );
                }
                return false;
            };
            // mm: convert values from object to an array of object
            const valueList = Object.keys(formPropsValues).reduce(
                (accumulator, key) => {
                    let value = formPropsValues[key];
                    //FM: check placeholder and replace computed value
                    if (
                        object &&
                        isBulk &&
                        (value === this.FIELD_PLACEHOLDER ||
                            checkPlaceholder(value))
                    ) {
                        let placeholderValue = null;
                        if (
                            object.isFile &&
                            isObject(value) &&
                            object.hasOwnProperty('hashes')
                        ) {
                            if (
                                Object.values(value).includes(
                                    this.ARTIFACT_PLACEHOLDER
                                )
                            ) {
                                placeholderValue = `artifact_${object.id}`;
                            }
                            const valueLabel = value.label;
                            if (
                                Object.keys(object.hashes).some(
                                    e => valueLabel === e.toUpperCase() // todo: missing case?
                                )
                            ) {
                                placeholderValue =
                                    object.hashes[valueLabel.toLowerCase()];
                            }
                        } else {
                            placeholderValue = object.endvalue ?? object.value;
                        }
                        value = placeholderValue;
                        if (!value) {
                            accumulator.push({
                                id: this.INVALID_ACTION_PLACEHOLDER,
                                value: this.INVALID_ACTION_PLACEHOLDER
                            });
                        }
                    }
                    // mm: flatten the values
                    if (value?.hasOwnProperty('value')) {
                        value = value.value;
                    }
                    accumulator.push({
                        id: key,
                        value: value
                    });
                    return accumulator;
                },
                []
            );
            // mm: if there are configuration fields
            if (object && actionFieldsByResource.length) {
                // mm: Check if there is at least one field with the object value
                const objectValue = object.isFile
                    ? `artifact_${object.id}`
                    : object.endvalue ?? object.value;
                // todo: reduce?
                const someFieldValueIsTheObjectValue = valueList
                    // mm: include only fields values
                    .filter(value => value.id.startsWith(fieldPrefix))
                    .some(value => {
                        // mm: consider valid value object hashes
                        if (
                            object.hasOwnProperty('hashes') &&
                            Object.values(object.hashes).includes(value.value)
                        ) {
                            return true;
                        }
                        return objectValue === value.value;
                    });
                if (!someFieldValueIsTheObjectValue && !isBulk) {
                    stickyAlert.notifyError({
                        title: <Translate message="Execute action" />,
                        message: (
                            <Translate message="There's no field using the current object, please fill at least one field with the object value" />
                        )
                    });
                    return false;
                }
            }
            // todo: reduce?
            const fieldsValues = valueList
                .filter(value => value.id.startsWith(fieldPrefix))
                .map(value => {
                    const selectedResource = this.getResourceById(
                        formPropsValues.resource
                    );
                    const str =
                        selectedResource &&
                        (selectedResource.integration_tag ||
                            selectedResource.integrationconfiguration_id);
                    const field = actionFields.find(field => {
                        return (
                            field.id === value.id &&
                            field.asset_type_only.includes(str)
                        );
                    });
                    return {
                        ...field,
                        value: value.value
                    };
                });

            const internalName = formPropsValues.action;
            const targetItem = this.props.actionList[
                formPropsValues.actionType
            ]?.find(e => internalName === e.internalName);

            const configurationValues =
                this.convertToConfigurationValues(fieldsValues);

            // console.log({ fieldsValues, configurationValues });

            const action = {
                id: targetItem.id,
                internalName: internalName,
                resourceId: formPropsValues.resource,
                configurationValues: configurationValues
            };

            //FM: check if action values are valid
            const checkInvalidAction = () => {
                const str = this.INVALID_ACTION_PLACEHOLDER;
                return valueList.some(e => Object.values(e).includes(str));
            };

            //FM: in case of invalid hash/values skip action execution
            if (isBulk && checkInvalidAction()) {
                return;
            }

            this.showOverlay();

            //FM: delay execution and return configuration value
            if (isBulk) {
                action.resource_id = object.id;
                action.type = object.getIntegerType();
                return action;
            }

            if (object && object.executeAction) {
                // RN: if from contextualMenu and Object not exist,
                // send resource_value to obtain the resource_id and permit to visit the relative entity url
                const objectValue = object.value;
                if (
                    objectValue &&
                    !object.id &&
                    object.extendedType &&
                    this.props.fromContextualMenu
                ) {
                    action.resource_value = objectValue;
                    action.type = object.extendedType;
                }
                object
                    .executeAction(action)
                    .then(response => {
                        this.hideOverlay();
                        if (200 === response.status) {
                            const entityId = response.data;
                            if (this.props.fromContextualMenu) {
                                //RN: go to result in another tab or in the same tab
                                this.props.onRunActionSubmit(
                                    this.state.openResultInOtherTab,
                                    entityId
                                );
                            } else {
                                this.props.onRunActionSubmit();
                            }
                            this.props.onHide?.();
                        }
                    })
                    .catch(exception => {
                        console.error(exception);
                        stickyAlert.notifyError({
                            title: <Translate message="Action execution" />,
                            message: toErrorMessage(exception)
                        });
                        this.hideOverlay();
                    });
            } else if (this.props.executeAction) {
                this.props.executeAction(action);
                this.hideOverlay();
                this.props.onHide?.();
            }
        };

        if (isBulk) {
            // get configuration values from all objects
            const configurations = [];
            let actionData = {};
            const objects = this.props.object;
            if (objects?.length) {
                const serialize = JSON.stringify;
                for (const object of objects) {
                    //FM: exclude invalid action
                    const actionConfig = prepareAndExecuteAction(object);
                    if (actionConfig) {
                        actionData = {
                            internalName: actionConfig.internalName,
                            resourceId: actionConfig.resourceId,
                            type: actionConfig.type,
                            id: actionConfig.id
                        };
                        configurations.push({
                            resource_id: actionConfig.resource_id,
                            configuration_values: serialize(
                                actionConfig.configurationValues
                            )
                        });
                    }
                }
            }
            ObjectModel.executeBulkActions(actionData, configurations)
                .catch(exception => {
                    console.error(exception);
                    stickyAlert.notifyError({
                        title: <Translate message="Bulk action execution" />,
                        message: toErrorMessage(exception)
                    });
                })
                .finally(() => {
                    this.hideOverlay();
                    this.props.onHide?.();
                });
        } else {
            prepareAndExecuteAction(this.props.object);
        }
    };

    setResources(resources, callback) {
        this.setState(
            {
                resources: resources
            },
            callback
        );
    }

    getActionsByType = () => {
        const newActionsByType = [];
        const { actionsByType } = this.state;
        if (actionsByType?.length) {
            const { floor, random } = Math;
            const randomic = () => floor(random() * 1e5 + 1);
            for (const action of actionsByType) {
                let newAction = action;
                const actionInternalName = action.internalName;
                if (
                    newActionsByType.find(
                        e => actionInternalName === e.internalName
                    )
                ) {
                    newAction = { ...action };
                    newAction.internalName = `${
                        newAction.internalName
                    }-${randomic()}${randomic()}`;
                }
                newActionsByType.push(newAction);
            }
        }
        return newActionsByType;
    };

    viewingFiles(children, files = []) {
        // todo: revise
        children.filter(child => {
            if (!child.isFolder) {
                files.push({
                    label: child.internalTitle,
                    value: `attachment_${child.label}`
                });
            } else {
                this.viewingFiles(child.children, files);
            }
        });
        return files;
    }

    render() {
        const formProps = this.props.formProps;
        const formPropsValues = formProps.values;
        const actionFieldsWithValues = this.props.actionFieldsByResource.map(
            field => {
                const fieldId = field.id;
                if (formPropsValues.hasOwnProperty(fieldId)) {
                    field.value = formPropsValues[fieldId];
                }
                return field;
            }
        );
        const section = this.props.section;
        const isObjectsSection = 'objects' === section;
        return (
            <>
                <Form className="h-100 flex-column d-flex overflow-hidden">
                    {isObjectsSection && (
                        <HeaderModal
                            icon="play-circle"
                            title={<Translate message="Execute action" />}
                            onHide={this.props.onHide}
                        />
                    )}
                    <ModalBody
                        section={section}
                        formProps={formProps}
                        values={
                            isObjectsSection
                                ? { ...formPropsValues } // ( prevents original from being reset )
                                : formPropsValues
                        }
                        setValues={this.setValues}
                        resources={this.state.resources}
                        setResource={this.setResource}
                        isInternalIntegration={this.state.isInternalIntegration}
                        integrationList={this.props.integrationList}
                        setIntegration={this.setIntegration}
                        setAction={this.setAction}
                        setActionType={this.setActionType}
                        actionsByType={this.getActionsByType()}
                        actionList={this.props.actionList}
                        disableChangeAction={this.props.disableChangeAction}
                        previousActionButtonFunction={
                            this.props.previousActionButtonFunction
                        }
                        actionFieldsByResource={
                            this.props.actionFieldsByResource
                        }
                        fetchingActionFieldsByResource={
                            this.state.fetchingActionFieldsByResource
                        }
                        fetchingActionList={this.props.fetchingActionList}
                        extraFieldOptions={[
                            {
                                id: 'artifact',
                                label: 'Artifact fields',
                                options: this.state.incidentArtifacts
                            },
                            {
                                id: 'incident',
                                label: 'Incident fields',
                                options: this.state.incidentFields
                            },
                            {
                                id: 'triage',
                                label: SETTINGS.triageSectionName,
                                options: this.state.incidentTriage
                            }
                        ]}
                        textAreaValSelection={this.props.textAreaValSelection}
                    />
                    {isObjectsSection && (
                        <ModalFooter
                            formProps={this.props.formProps}
                            resources={this.state.resources}
                            actionFields={actionFieldsWithValues}
                            onSubmit={this.onSubmit}
                            fromContextualMenu={this.props.fromContextualMenu}
                            chooseTabData={{
                                openResultInOtherTab:
                                    this.state.openResultInOtherTab,
                                setOpenResultInOtherTab:
                                    this.setOpenResultInNewTabValue
                            }}
                        />
                    )}
                </Form>
                <PageOverlay
                    show={this.state.showOverlay}
                    text={<Translate message="Processing action execution" />}
                />
            </>
        );
    }
}

ModalWrapper.propTypes = {
    formProps: PropTypes.object.isRequired,
    stickyAlert: PropTypes.object.isRequired,
    object: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    actionFieldsByResource: PropTypes.array.isRequired,
    actionFields: PropTypes.array.isRequired,
    setActionFieldsByResource: PropTypes.func.isRequired,
    setActionFields: PropTypes.func.isRequired,
    actionList: PropTypes.object,
    tokenList: PropTypes.object,
    fieldPrefix: PropTypes.string,
    onRunActionSubmit: PropTypes.func,
    previousActionButtonFunction: PropTypes.func,
    executeAction: PropTypes.func,
    onHide: PropTypes.func,
    fromContextualMenu: PropTypes.bool,
    isEditAction: PropTypes.bool,
    isBulk: PropTypes.bool,
    fetchingActionList: PropTypes.bool,
    actionFieldsMappingKey: PropTypes.string
};

ModalWrapper.defaultProps = {
    fieldPrefix: 'field__',
    disableChangeAction: false,
    tokenList: {},
    fillListOptions: false,
    isBulk: false,
    isEditAction: false
};

ModalWrapper.contextType = IncidentContext;

export default ModalWrapper;
